import { Sprite } from "../../../display/Sprite";
import { Matrix4x4 } from "../../../maths/Matrix4x4";
import { Vector2 } from "../../../maths/Vector2";
import { Vector3 } from "../../../maths/Vector3";
import { RenderTargetFormat } from "../../../RenderEngine/RenderEnum/RenderTargetFormat";
import { RenderTexture2D } from "../../../resource/RenderTexture2D";
import { Ray } from "../../math/Ray";
import { MaterialRenderMode } from "../../../resource/Material";
import { MeshSprite3DShaderDeclaration } from "../MeshSprite3DShaderDeclaration";
import { BaseRender } from "../render/BaseRender";
import { RenderElement } from "../render/RenderElement";
import { Sprite3D } from "../Sprite3D";
import { UI3DGeometry } from "./UI3DGeometry";
import { Event } from "../../../events/Event";
import { UnlitMaterial } from "../material/UnlitMaterial";
import { InputManager } from "../../../events/InputManager";
import { NodeFlags } from "../../../Const";
import { ILaya } from "../../../../ILaya";
import { Vector4 } from "../../../maths/Vector4";
import { LayaEnv } from "../../../../LayaEnv";
import { RenderState } from "../../../RenderDriver/RenderModuleData/Design/RenderState";
import { LayaGL } from "../../../layagl/LayaGL";
import { Quaternion } from "../../../maths/Quaternion";
import { Utils } from "../../../utils/Utils";
import { Rectangle } from "../../../maths/Rectangle";
import { SpriteUtils } from "../../../utils/SpriteUtils";
import { RepaintFlag } from "../../../display/SpriteConst";
class UI3DShellSprite extends Sprite {
    get genMipMap() {
        return this._genMipMap;
    }
    set genMipMap(value) {
        this._genMipMap = value;
    }
    get rtWidth() {
        return this._rtWidth;
    }
    set rtWidth(value) {
        this._rtWidth = value;
    }
    get rtHeight() {
        return this._rtHeight;
    }
    set rtHeight(value) {
        this._rtHeight = value;
    }
    get invertY() {
        return this._invertY;
    }
    set invertY(value) {
        this._invertY = value;
    }
    constructor() {
        super();
        this.updateRT = false;
        this._genMipMap = false;
    }
    updateRenderTexture() {
        let rect = Rectangle.TEMP;
        if (this.mask) {
            SpriteUtils.getRect(this.mask, false, rect);
            rect.x += this.mask._pivotX;
            rect.y += this.mask._pivotY;
        }
        else {
            SpriteUtils.getRect(this, false, rect);
            rect.x += this._pivotX;
            rect.y += this._pivotY;
        }
        let oldRT = this._drawOriRT;
        let maskRect = this._subStructRender._rtRect;
        if (oldRT) {
            if (maskRect.width === rect.width && maskRect.height === rect.height) {
                return false;
            }
            oldRT.destroy();
        }
        let renderTexture;
        if (this._genMipMap) {
            renderTexture = new RenderTexture2D(this._rtWidth, this._rtHeight, RenderTargetFormat.R8G8B8A8);
            renderTexture._renderTarget && renderTexture._renderTarget.dispose();
            renderTexture._renderTarget = LayaGL.textureContext.createRenderTargetInternal(renderTexture.width, renderTexture.height, renderTexture.getColorFormat(), renderTexture.depthStencilFormat, true, false, 1, false);
            renderTexture._texture = renderTexture._renderTarget._textures[0];
            renderTexture._texture.gammaCorrection = 2.2;
        }
        else {
            renderTexture = new RenderTexture2D(this._rtWidth, this._rtHeight, RenderTargetFormat.R8G8B8A8);
        }
        renderTexture._invertY = this._invertY;
        this._drawOriRT = renderTexture;
        this.updateRT = true;
        return true;
    }
}
export class UI3D extends BaseRender {
    get genMipMap() {
        return this._genMipMap;
    }
    set genMipMap(value) {
        this._genMipMap = value;
        this._shellSprite.genMipMap = value;
        this._shellSprite.repaint(RepaintFlag.UpdateRT);
    }
    get sprite() {
        return this._uisprite;
    }
    set sprite(value) {
        if (value == this._uisprite)
            return;
        this._uisprite = value;
        this._shellSprite.removeChildren(0, this._shellSprite.numChildren - 1);
        if (value)
            this._shellSprite.addChild(value);
        this._resizeRT();
        this._shellSprite.repaint();
        this.boundsChange = true;
    }
    get prefab() {
        return this._prefab;
    }
    set prefab(value) {
        this._prefab = value;
        if (value)
            this.sprite = value.create();
        else
            this.sprite = null;
    }
    get scale() {
        return this._size;
    }
    set scale(value) {
        if (value.x <= 0 || value.y <= 0 || Vector2.equals(value, this._size))
            return;
        value.cloneTo(this._size);
        this._resizeRT();
        this.boundsChange = true;
        this._scale.setValue(value.x, value.y, 1);
    }
    get renderMode() {
        if (!this.sharedMaterial)
            this.sharedMaterial = this._ui3DMat;
        return this.sharedMaterial.materialRenderMode;
    }
    set renderMode(value) {
        this.sharedMaterial.materialRenderMode = value;
        this.boundsChange = true;
    }
    get cull() {
        let mat = this.sharedMaterial;
        if (!mat) {
            mat = this._ui3DMat;
        }
        return mat.cull;
    }
    set cull(value) {
        this.sharedMaterial && (this.sharedMaterial.cull = value);
    }
    get resolutionRate() {
        return this._resolutionRate;
    }
    set resolutionRate(value) {
        if (value <= 0)
            return;
        if (this._resolutionRate == value)
            return;
        this._resolutionRate = value;
        this._resizeRT();
    }
    get billboard() {
        return this._view;
    }
    set billboard(value) {
        this._view = value;
        this._sizeChange = true;
        this.boundsChange = true;
    }
    get enableHit() {
        return this._hit;
    }
    set enableHit(value) {
        this._hit = value;
    }
    set cameraSpace(value) {
        if (this._cameraSpace == value) {
            return;
        }
        this._cameraSpace = value;
        this._camera && this._resizeRT();
    }
    get cameraSpace() {
        return this._cameraSpace;
    }
    set cameraPlaneDistance(value) {
        value = Math.max(0, value);
        this._cameraPlaneDistance = value;
    }
    get cameraPlaneDistance() {
        return this._cameraPlaneDistance;
    }
    set attachCamera(value) {
        if (this._camera == value)
            return;
        this._camera = value;
        this._cameraSpace && this._resizeRT();
    }
    get attachCamera() {
        return this._camera;
    }
    constructor() {
        super();
        this._sizeChange = true;
        this._view = true;
        this._bindPropertyName = "u_AlbedoTexture";
        this._hit = false;
        this._cameraSpace = false;
        this._worldParams = new Vector4();
        this._genMipMap = false;
        this._size = new Vector2(1, 1);
        this._resolutionRate = 128;
        this._shellSprite = new UI3DShellSprite();
        this._shellSprite.name = "UI3D";
        this._shellSprite.cacheAs = "bitmap";
        this._shellSprite._setBit(NodeFlags.DISPLAYED_INSTAGE, true);
        this._shellSprite._setBit(NodeFlags.ACTIVE_INHIERARCHY, true);
        this._shellSprite._parent = ILaya.stage;
        this._baseRenderNode.shaderData.addDefine(MeshSprite3DShaderDeclaration.SHADERDEFINE_UV0);
        this._matrix = new Matrix4x4();
        this._scale = new Vector3(1.0, 1.0, 1.0);
    }
    _creatDefaultMat() {
        if (this._ui3DMat)
            return;
        this._ui3DMat = new UnlitMaterial();
        this._ui3DMat.materialRenderMode = MaterialRenderMode.RENDERMODE_OPAQUE;
        this._ui3DMat.cull = RenderState.CULL_BACK;
    }
    _addRenderElement() {
        var elements = this._renderElements;
        if (!this.sharedMaterial) {
            this._creatDefaultMat();
            this.sharedMaterial = this._ui3DMat;
        }
        this._setMaterialTexture();
        var material = this.sharedMaterial;
        var element = new RenderElement();
        element.setTransform(this.owner._transform);
        element.render = this;
        element.material = material;
        element.renderSubShader = element.material.shader.getSubShaderAt(0);
        this._geometry = new UI3DGeometry(this);
        element.setGeometry(this._geometry);
        elements.push(element);
        this._setRenderElements();
        this.geometryBounds = this._geometry.bounds;
    }
    _isCameraSpaceMode() {
        return this._cameraSpace && this._camera;
    }
    _parseHit(ray) {
        let _tempRay = UI3D._ray;
        let u, v;
        if (!this._uisprite || !LayaEnv.isPlaying)
            return null;
        this._matrix.invert(tempMatrix);
        Vector3.transformCoordinate(ray.origin, tempMatrix, _tempRay.origin);
        Vector3.TransformNormal(ray.direction, tempMatrix, _tempRay.direction);
        _tempRay.direction.normalize();
        let normalizeHitWidth = 0;
        let normalizeHitHeight = 0;
        let t = -_tempRay.origin.z / _tempRay.direction.z;
        if (t < 0) {
            return null;
        }
        else {
            u = _tempRay.origin.x + t * _tempRay.direction.x;
            v = _tempRay.origin.y + t * _tempRay.direction.y;
        }
        normalizeHitWidth = u + 0.5;
        normalizeHitHeight = v + 0.5;
        let cx = normalizeHitWidth * this._rendertexure2D.width;
        let cy = (1.0 - normalizeHitHeight) * this._rendertexure2D.height;
        let target = InputManager.inst.getSpriteUnderPoint(this._uisprite, cx, cy);
        if (target)
            return target;
        else
            return null;
    }
    _resizeRT() {
        let width;
        let height;
        if (this._isCameraSpaceMode()) {
            width = this._camera.viewport.width;
            height = this._camera.viewport.height;
        }
        else {
            width = this._size.x * this._resolutionRate;
            height = this._size.y * this._resolutionRate;
        }
        if (this._shellSprite && (this._shellSprite.rtWidth !== width || this._shellSprite.rtHeight !== height)) {
            this._shellSprite && (this._shellSprite.rtWidth = width);
            this._shellSprite && (this._shellSprite.rtHeight = height);
            this._shellSprite && (this._shellSprite.invertY = !LayaGL.renderEngine._screenInvertY);
            this._shellSprite && this._shellSprite.repaint(RepaintFlag.UpdateRT);
            this._setMaterialTexture();
        }
    }
    onPreRender() {
        if (this._isCameraSpaceMode()) {
            this.boundsChange = true;
            let cameraforward = Vector3.TEMP;
            let rotate = Quaternion.TEMP;
            let scale = tempVec3;
            let camera = this._camera;
            camera.transform.getForward(cameraforward);
            cameraforward = cameraforward.normalize();
            Vector3.scale(cameraforward, this._cameraPlaneDistance, cameraforward);
            Vector3.add(camera.transform.position, cameraforward, cameraforward);
            camera.transform.rotation.cloneTo(rotate);
            if (camera.orthographic) {
                scale.setValue(camera.orthographicVerticalSize * camera.aspectRatio, camera.orthographicVerticalSize, 1);
            }
            else {
                let height = Math.tan(Utils.toRadian(camera.fieldOfView / 2)) * this._cameraPlaneDistance * 2;
                scale.setValue(height * camera.aspectRatio, height, 1);
            }
            Matrix4x4.createAffineTransformation(cameraforward, rotate, scale, this._matrix);
        }
        else {
            if (this.billboard) {
                this._sizeChange = false;
                let camera = this.owner.scene.cullInfoCamera;
                Matrix4x4.createAffineTransformation(this._transform.position, camera.transform.rotation, this._scale, this._matrix);
            }
            else if (this._sizeChange) {
                this.boundsChange = true;
                this._transform.worldMatrix.cloneTo(this._matrix);
            }
        }
    }
    getUITexture() {
        return this._rendertexure2D;
    }
    _getCameraDistance(rayOri) {
        return Vector3.distance(rayOri, this.owner.transform.position);
    }
    _renderUpdate(context) {
        let shaderData = this._baseRenderNode.shaderData;
        shaderData.setMatrix4x4(Sprite3D.WORLDMATRIX, this._matrix);
        let transform = this.owner.transform;
        let worldParams = this._worldParams;
        worldParams.x = transform.getFrontFaceValue();
        shaderData.setVector(Sprite3D.WORLDINVERTFRONT, worldParams);
    }
    renderUpdate(context) {
        this._renderElements.forEach(element => {
            let geometry = element._geometry;
            element._renderElementOBJ.isRender = geometry._prepareRender(context);
            geometry._updateRenderParams(context);
        });
    }
    _submitRT() {
        if (this._shellSprite.updateRT) {
            this._shellSprite.updateRT = false;
            this._setMaterialTexture();
        }
    }
    _setMaterialTexture() {
        if (this._shellSprite._drawOriRT) {
            this.sharedMaterial.addDefine(UnlitMaterial.SHADERDEFINE_ALBEDOTEXTURE);
            this.sharedMaterial.setTexture(this._bindPropertyName, this._shellSprite._drawOriRT);
            this._rendertexure2D = this._shellSprite._drawOriRT;
        }
        else {
            this.sharedMaterial.removeDefine(UnlitMaterial.SHADERDEFINE_ALBEDOTEXTURE);
        }
    }
    _checkUIPos(ray) {
        if (!this.enableHit)
            return false;
        return this._parseHit(ray);
    }
    _calculateBoundingBox() {
        this._geometry.bounds._tranform(this._matrix, this._bounds);
    }
    _onAdded() {
        super._onAdded();
        this._addRenderElement();
    }
    _onDisable() {
        super._onDisable();
        this.owner.transform.off(Event.TRANSFORM_CHANGED, this, this._transByRotate);
        this.owner.scene._UI3DManager.remove(this);
    }
    _onEnable() {
        super._onEnable();
        this.owner.scene._UI3DManager.add(this);
        this.owner.transform.on(Event.TRANSFORM_CHANGED, this, this._transByRotate);
        this._transByRotate();
    }
    _onDestroy() {
        super._onDestroy();
        this._rendertexure2D && this._rendertexure2D.destroy();
        this._shellSprite && (this._shellSprite._parent = null);
        this._uisprite && this._uisprite.destroy();
        this._shellSprite && this._shellSprite.destroy();
        this._ui3DMat && this._ui3DMat.destroy();
        this._resolutionRate = null;
        this._size = null;
        this._scale = null;
        this._matrix = null;
    }
    _transByRotate() {
        if (!this.billboard) {
            this._sizeChange = true;
        }
        this.boundsChange = true;
    }
}
UI3D.DEBUG = false;
UI3D._ray = new Ray(new Vector3(), new Vector3());
const tempMatrix = new Matrix4x4();
const tempVec3 = new Vector3();
